import { useEffect } from 'react';
import {
  Flex,
  Typography,
  Form,
  FormInstance,
  Input,
} from 'antd';
import { Card } from '../Card';
import { Heading } from '../Heading';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddNewLocationModal } from '../AddNewLocationModal';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import { Store } from 'antd/es/form/interface';
import { getWarehouseAddressType } from '@/utils';
import { CustomSelect } from '../CustomSelect';
import { PickupAddress } from '@/types';
const { Text } = Typography;

interface PickupAddressCardProps {
  form: FormInstance;
  orderStatus?: string;
  previewOrderPrice: (orderPayload: Store) => void;
}

const PickupAddressCard = ({ form, previewOrderPrice, orderStatus }: PickupAddressCardProps) => {
  const [selectedValue, setSelectedValue] = useState('PICK_UP');
  const [addNewLocationModalOpen, setAddNewLocationModalOpen] = useState(false);
  const { t } = useTranslation();

  const handleOptionSelect = (value: string) => {
    setSelectedValue(value);
    form.setFieldsValue({ pickupOrDropoff: value, saved_pick_up_address_uid: '' });
  };

  const { data: pickupAddresses, isLoading: pickupAddressLoading } = useFetchPickupAddresses();

  const typedSelectData: PickupAddress[] = getWarehouseAddressType(pickupAddresses?.object?.items);

  useEffect(() => {
    const initPickupType = form.getFieldValue('pickupOrDropoff') ?? 'PICK_UP';
    if (initPickupType) {
      form.setFieldsValue({
        pickupOrDropoff: initPickupType,
      });
      setSelectedValue(initPickupType);
    }
  }, [form]);

  useEffect(() => {
    if (!orderStatus) {
      // if !orderStatus so it's not editing
      const saved_pick_up_address_uid = form.getFieldValue('saved_pick_up_address_uid');

      if (typedSelectData.length > 0 && !saved_pick_up_address_uid) {
        const defaultAddress = typedSelectData.find((item) => !!item.default)?.uid;
        form.setFieldsValue({ saved_pick_up_address_uid: defaultAddress });
      }
    }
  }, [typedSelectData, form]);

  const handleSelectPickupAddress = (uid: string) => {
    form.setFieldsValue({ saved_pick_up_address_uid: uid });
    const formValues = form.getFieldsValue();

    previewOrderPrice({ ...formValues, saved_pick_up_address_uid: uid });
  };

  return (
    <Card>
      <div className='flex items-center mb-4'>
        <Heading
          title={`${t('pickup_dropoff')}`}
          fontSize='text-xs'
          fontWeight='font-bold'
          color='text-sd-muted-foreground'
          extraClassName='uppercase tracking-wider'
        />
        <span className='text-sd-destructive text-xs mx-1'>*</span>
      </div>
      <Form.Item name='pickupOrDropoff' noStyle>
        <input type='hidden' />
      </Form.Item>
      {/* O-21: Removed BoxyCollect radio — PICK_UP is the only option */}
      <div>
          {typedSelectData.length !== 0 ? (
            <>
              {!orderStatus || orderStatus === 'new' ? (
                <CustomSelect
                  formItemName='saved_pick_up_address_uid'
                  placeholder={t('select_pickup')}
                  isLoading={pickupAddressLoading}
                  data={typedSelectData.map((item) => ({
                    ...item,
                    description: item.address_text,
                  }))}
                  title={t('boxy_agent_will_collect')}
                  titleStyle='font-normal text-xs text-sd-muted-foreground mb-3'
                  parentStyle='mt-0'
                  onSelect={handleSelectPickupAddress}
                />
              ) : (
                <>
                  <Form.Item name='saved_pick_up_address_uid' />
                  <Text>{t('pickup_location')}</Text>
                  <Form.Item name='freezed_pick_up_address_title'>
                    <Input className='h-10 rounded-md' disabled />
                  </Form.Item>
                </>
              )}
            </>
          ) : (
            <Flex vertical>
              <Text className='font-normal text-xs text-sd-muted-foreground mb-3'>
                {t('boxy_agent_will_collect')}
              </Text>
              <Flex
                className='rounded-md gap-x-1 py-4 ps-4 border border-solid border-sd-input'
              >
                <Text className='font-normal text-sm text-sd-foreground'>
                  {t('there_is_no_address_in_your_list')}
                </Text>
                <Text
                  onClick={() => {
                    setAddNewLocationModalOpen((prev) => !prev);
                  }}
                  className='font-normal cursor-pointer text-base leading-[20.48px] capitalize text-customBlue'
                >
                  {t('address_list')}
                </Text>
              </Flex>
            </Flex>
          )}
        </div>
      <AddNewLocationModal
        visible={addNewLocationModalOpen}
        onClose={() => setAddNewLocationModalOpen(false)}
        updatePreviewOrderPrice={(addressUid?: string) =>
          addressUid && handleSelectPickupAddress(addressUid)
        }
      />
    </Card>
  );
};

export default PickupAddressCard;
